import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getCountFromServer, limit, orderBy, query, where } from 'firebase/firestore';
import {
  Archive, ArrowRight, CarFront, CirclePlus, PoundSterling, Search, type LucideIcon,
} from 'lucide-react';
import { JobRow, JobRowSkeleton } from '../components/JobRow';
import { GroupLabel } from '../components/ui/Card';
import { Alert } from '../components/ui/Alert';
import { ReadFailure } from '../components/ReadFailure';
import { listenQuery } from '../lib/liveRead';
import { withReadTimeout } from '../lib/readLoading';
import { db } from '../lib/firebase';
import { missingCompletionFields } from '../lib/completion';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../auth/AuthProvider';
import { buildJobReadConstraints, finishedJobWindow } from '../lib/jobScope';
import {
  canCreateJobs, canInvoiceJobs, canPriceJobs, canViewJobFinancials,
  JOB_ARCHIVE_STATES, JOB_LIVE_STATES, type Job,
} from '../types/models';

interface Counts {
  pending: number;
  inProgress: number;
  archived: number;
  awaitingPrice?: number;
}

function greeting(now: Date): string {
  const hour = now.getHours();
  if (hour < 12) return 'Morning';
  if (hour < 18) return 'Afternoon';
  return 'Evening';
}

export default function DashboardPage() {
  usePageTitle('Home');
  const { activeCompanyId, activeRole, memberships, user } = useAuth();
  const [counts, setCounts] = useState<Counts | null>(null);
  const [liveJobs, setLiveJobs] = useState<Job[]>([]);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentError, setRecentError] = useState<string | null>(null);
  const [readAttempt, setReadAttempt] = useState(0);

  const role = activeRole ?? undefined;
  const canCreate = canCreateJobs(role);
  const canUseAccounts = canPriceJobs(role) || canInvoiceJobs(role);
  const showFinancials = canViewJobFinancials(role);

  useEffect(() => {
    if (!activeCompanyId) { setCounts(null); setLoadingCounts(false); return; }
    let cancelled = false;
    setLoadingCounts(true);
    setCounts(null);
    setError(null);
    const jobsRef = collection(db, 'companies', activeCompanyId, 'jobs');
    const scope = buildJobReadConstraints({ activeCompanyId, role: activeRole, uid: user?.uid, memberships });
    if (scope.blocked) { setError(scope.error); setLoadingCounts(false); return; }
    (async () => {
      try {
        const [pending, inProgress, archived] = await withReadTimeout(Promise.all([
          getCountFromServer(query(jobsRef, where('state', '==', 'pending'), ...scope.constraints)),
          getCountFromServer(query(jobsRef, where('state', '==', 'in_progress'), ...scope.constraints)),
          getCountFromServer(query(jobsRef, where('state', 'in', JOB_ARCHIVE_STATES), ...scope.constraints, ...finishedJobWindow(activeRole))),
        ]));
        if (cancelled) return;
        setCounts({
          pending: pending.data().count,
          inProgress: inProgress.data().count,
          archived: archived.data().count,
        });
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setError(e instanceof Error ? e.message : 'Failed to load the job counts.');
      } finally {
        if (!cancelled) setLoadingCounts(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeCompanyId, activeRole, memberships, user?.uid, readAttempt]);

  // The accounts queue count is a second, narrower query. It is allowed to fail
  // quietly: the Accounts tile simply carries no badge, which is better than a
  // whole failed screen if its composite index is ever missing.
  useEffect(() => {
    if (!activeCompanyId || !canUseAccounts) return;
    let cancelled = false;
    const jobsRef = collection(db, 'companies', activeCompanyId, 'jobs');
    const scope = buildJobReadConstraints({ activeCompanyId, role: activeRole, uid: user?.uid, memberships });
    if (scope.blocked) return;
    (async () => {
      try {
        const snap = await withReadTimeout(getCountFromServer(query(
          jobsRef,
          where('accountingStatus', '==', 'unpriced'),
          where('state', 'in', JOB_ARCHIVE_STATES),
          ...scope.constraints,
        )));
        if (!cancelled) setCounts((current) => (current ? { ...current, awaitingPrice: snap.data().count } : current));
      } catch (e) {
        console.warn('Accounts queue count unavailable', e);
      }
    })();
    return () => { cancelled = true; };
  }, [activeCompanyId, activeRole, canUseAccounts, memberships, user?.uid, readAttempt, loadingCounts]);

  useEffect(() => {
    if (!activeCompanyId) { setLiveJobs([]); setLoadingRecent(false); return; }
    setLoadingRecent(true);
    setLiveJobs([]);
    setRecentError(null);
    const jobsRef = collection(db, 'companies', activeCompanyId, 'jobs');
    const scope = buildJobReadConstraints({ activeCompanyId, role: activeRole, uid: user?.uid, memberships });
    if (scope.blocked) { setRecentError(scope.error); setLoadingRecent(false); return; }
    const q = query(
      jobsRef,
      where('state', 'in', JOB_LIVE_STATES),
      ...scope.constraints,
      orderBy('createdAt', 'desc'),
      limit(8)
    );
    const unsub = listenQuery(q, (snap) => {
      setLiveJobs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Job, 'id'>) })));
      setLoadingRecent(false);
      setRecentError(null);
    }, (err) => { setRecentError(err.message); setLoadingRecent(false); });
    return () => unsub();
  }, [activeCompanyId, activeRole, memberships, user?.uid, readAttempt]);

  const liveCount = counts ? counts.pending + counts.inProgress : undefined;
  const firstName = (user?.displayName || '').trim().split(/\s+/)[0];
  const owed = useMemo(
    () => liveJobs
      .map((job) => ({ job, missing: missingCompletionFields(job) }))
      .filter((entry) => entry.missing.length > 0),
    [liveJobs]
  );

  // The subhead carries the two numbers that decide what happens next.
  const headline = [
    liveCount === undefined ? null : `${liveCount} job${liveCount === 1 ? '' : 's'} live`,
    canUseAccounts && counts?.awaitingPrice
      ? `${counts.awaitingPrice} completed job${counts.awaitingPrice === 1 ? '' : 's'} awaiting a price`
      : null,
  ].filter(Boolean).join(' · ');

  return (
    <div>
      <h1 className="text-[32px] font-bold leading-[38px] tracking-[-0.03em] text-ink">
        {greeting(new Date())}{firstName ? `, ${firstName}` : ''}
      </h1>
      <p className="mt-1.5 min-h-[22px] text-[15px] text-ink2">{headline}</p>

      {!activeCompanyId && <div className="mt-4"><Alert tone="warning">No active company selected.</Alert></div>}
      {error && <div className="mt-4"><ReadFailure message={error} retry={() => setReadAttempt((n) => n + 1)} /></div>}

      {/* One filled tile — the thing you came to do. A tile a role cannot use is
          absent and the grid reflows, rather than being shown and refused. */}
      <div className="mt-5 grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-3">
        {canCreate && (
          <Tile to="/jobs/new" icon={CirclePlus} title="New job" detail="Registration first, details later" primary />
        )}
        <Tile to="/jobs" icon={CarFront} title="Live jobs"
          detail={counts ? `${liveCount} live · ${counts.pending} pending` : 'Pending and in progress'}
          loading={loadingCounts} />
        <Tile to="/archive" icon={Archive} title="Archive"
          detail={counts ? `${counts.archived} completed` : 'Completed jobs'} loading={loadingCounts} />
        {canCreate && <Tile to="/lookup" icon={Search} title="Reg check" detail="DVLA and MOT lookup" />}
        {canUseAccounts && (
          <Tile to="/accounts" icon={PoundSterling} title="Accounts" detail="Price and invoice" badge={counts?.awaitingPrice} />
        )}
      </div>

      <div className="mt-2 grid items-start gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <section>
          <GroupLabel actions={
            <Link to="/jobs" className="inline-flex items-center gap-1 text-[14px] font-semibold text-accent hover:underline">
              All live jobs <ArrowRight size={15} />
            </Link>
          }>
            Latest live jobs
          </GroupLabel>
          {recentError && <div className="mb-3"><ReadFailure message={recentError} retry={() => setReadAttempt((n) => n + 1)} /></div>}
          <div className="flex flex-col gap-2">
            {loadingRecent ? (
              Array.from({ length: 4 }).map((_, i) => <JobRowSkeleton key={i} />)
            ) : recentError ? null : liveJobs.length === 0 ? (
              <p className="rounded-[14px] border border-hair bg-elev px-4 py-8 text-center text-[15px] text-ink2">
                No live jobs to show.
              </p>
            ) : (
              liveJobs.slice(0, 5).map((job) => (
                <JobRow key={job.id} job={job} to={`/jobs/${job.id}`} showFinancials={showFinancials} />
              ))
            )}
          </div>
        </section>

        <section>
          <GroupLabel>Needs details</GroupLabel>
          <div className="overflow-hidden rounded-[14px] border border-hair bg-elev">
            {owed.length === 0 ? (
              <p className="px-4 py-6 text-center text-[14.5px] text-ink2">
                {loadingRecent ? 'Checking…' : 'Every live job has what it needs to complete.'}
              </p>
            ) : (
              owed.map(({ job, missing }) => (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="flex items-center gap-3 border-t border-rowhair px-4 py-3 first:border-t-0 hover:bg-hover"
                >
                  <span className="min-w-0">
                    <span className="reg block text-[14.5px] text-ink">{job.registrationNumber || 'No reg'}</span>
                    <span className="mt-0.5 block truncate text-[13px] text-outstanding">
                      Missing {missing.slice(0, 3).map((m) => m.label).join(', ')}
                      {missing.length > 3 ? ` and ${missing.length - 3} more` : ''}
                    </span>
                  </span>
                  <ArrowRight size={16} className="ml-auto shrink-0 text-ink3" />
                </Link>
              ))
            )}
          </div>
          <p className="ml-1 mt-2.5 text-[13px] text-ink3">
            Amber means required, not yet supplied. It goes as soon as the detail is added.
          </p>
        </section>
      </div>
    </div>
  );
}

function Tile({
  to, icon: Icon, title, detail, primary = false, badge, loading = false,
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  detail: string;
  primary?: boolean;
  badge?: number;
  loading?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`flex h-28 flex-col justify-between rounded-[14px] border p-4 transition-colors duration-[180ms] ease-out ${
        primary ? 'border-accent bg-accent text-onaccent' : 'border-hair bg-elev text-accent hover:border-strong'
      }`}
    >
      <span className="flex items-start justify-between">
        <Icon size={22} strokeWidth={1.8} aria-hidden />
        {!!badge && (
          <span className="inline-flex h-[22px] min-w-[26px] items-center justify-center rounded-full bg-outstanding px-2 font-mono text-[12px] font-semibold text-white">
            {badge}
          </span>
        )}
      </span>
      <span>
        <span className={`block text-[17px] font-semibold tracking-[-0.01em] ${primary ? 'text-onaccent' : 'text-ink'}`}>{title}</span>
        <span className={`mt-0.5 block text-[13px] ${primary ? 'text-onaccent opacity-85' : 'text-ink3'}`}>
          {loading ? '…' : detail}
        </span>
      </span>
    </Link>
  );
}
